import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import useEscapeKey from '../hooks/useEscapeKey'
import useFocusTrap from '../hooks/useFocusTrap'
import './ProjectSelector.css'

const ProjectSelector = ({ projects, isOpen, onClose }) => {
  const navigate = useNavigate()
  const dialogRef = useFocusTrap(isOpen)
  useEscapeKey(onClose, isOpen)

  if (!isOpen) return null

  const handleProjectSelect = (project) => {
    // Get the project ID from either projectId or id property
    const projectId = project.projectId || project.id

    if (!projectId) {
      console.error('Project ID is undefined:', project)
      return
    }

    onClose()
    navigate(`/edit/${projectId}`)
  }

  // Use createPortal to render directly to the document body
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{
        background: 'rgba(10, 14, 22, 0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        animation: 'projsel-backdrop-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="projsel-title"
        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{
          animation: 'projsel-modal-in 320ms cubic-bezier(0.22, 1, 0.36, 1) both',
          transformOrigin: 'center center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 bg-gradient-to-r from-blue-500 to-cyan-400">
          <h2 id="projsel-title" className="text-2xl font-bold text-white text-center">Select a Project</h2>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {projects.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No projects available</p>
            </div>
          ) : (
            <div className="space-y-3">
              {projects.map((project) => {
                const projectId = project.projectId || project.id

                return (
                  <button
                    key={projectId}
                    onClick={() => handleProjectSelect(project)}
                    className="w-full text-left p-4 rounded-lg transition-all duration-200 flex items-center hover:bg-blue-50 border border-gray-100 hover:border-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-400 to-cyan-300 flex items-center justify-center text-white font-semibold text-lg shadow-sm flex-shrink-0">
                      {project.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="font-medium text-gray-800">{project.name || 'Unnamed Project'}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {project.description
                          ? project.description.length > 60
                            ? project.description.substring(0, 60) + '...'
                            : project.description
                          : 'No description'}
                      </p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

ProjectSelector.propTypes = {
  projects: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ProjectSelector
